import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Calendar, RefreshCw } from 'lucide-react';
import ReportHeader from '@/components/ReportHeader';
import { apiService } from '@/services/api';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';

const Container = styled.div`
  padding: 16px;
`;

const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const Select = styled.select`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const Acoes = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const Botao = styled.button`
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const Section = styled.section`
  padding: 12px 16px 4px 16px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 10px 0 8px 0;
`;


const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
`;

type BaseCalculo = 'valor' | 'quantidade' | 'lucro';
type TipoCurva = 'produto' | 'grupo' | 'medida';

type CurvaItem = {
  id: string;
  nome: string;
  grupo?: string;
  quantidade: number;
  valor: number;
  lucro: number;
  base: number;
  perc: number;
  percAcum: number;
  classe: 'A' | 'B' | 'C';
  rank: number;
};

const toNumber = (v: any): number => {
  if (v === null || v === undefined || v === '') return 0;
  const num = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return isNaN(num) ? 0 : num;
};

const formatMoeda = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatQuantidade = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

const montarCurva = (rows: any[], tipo: TipoCurva, base: BaseCalculo, ordenarAsc: boolean): CurvaItem[] => {
  const mapa = new Map<string, { id: string; nome: string; grupo?: string; quantidade: number; valor: number; lucro: number }>();

  rows.forEach((r) => {
    const quantidade = toNumber(r?.Quantidade);
    const preco = toNumber(r?.PrecoComTaxaCorreta ?? r?.ValorCobradoProdutoUnitario ?? r?.ValorUnitarioProduto);
    const desconto = toNumber(r?.VlrDesconto);
    const custoUnit = toNumber(r?.CustoUnitario ?? r?.ValorCobradoProdutoUnitario ?? 0);
    const valor = (preco * quantidade) - desconto;
    const lucro = valor - (custoUnit * quantidade);

    const nome = tipo === 'produto'
      ? String(r?.Produto ?? '').trim()
      : tipo === 'grupo'
        ? String(r?.Grupo ?? '').trim()
        : String(r?.Medida ?? r?.MedidaId ?? '').trim();
    const grupoNome = String(r?.Grupo ?? '').trim();
    const idBase = tipo === 'produto'
      ? (r?.Controle !== undefined && r?.Controle !== null ? String(r.Controle) : nome)
      : tipo === 'medida'
        ? (r?.MedidaId !== undefined && r?.MedidaId !== null ? String(r.MedidaId) : nome)
        : nome;
    const id = idBase && idBase.trim() !== '' ? idBase : `sem-${tipo}`;
    if (!mapa.has(id)) {
      mapa.set(id, {
        id,
        nome: nome || (tipo === 'produto' ? 'Sem produto' : 'Sem grupo'),
        grupo: grupoNome || 'Sem grupo',
        quantidade: 0,
        valor: 0,
        lucro: 0
      });
    }
    const item = mapa.get(id)!;
    item.quantidade += quantidade;
    item.valor += valor;
    item.lucro += lucro;
  });

  const items = Array.from(mapa.values());
  const totalBase = items.reduce((sum, item) => {
    if (base === 'lucro') return sum + item.lucro;
    return sum + (base === 'valor' ? item.valor : item.quantidade);
  }, 0);
  const ordenados = items.sort((a, b) => {
    const va = base === 'lucro' ? a.lucro : (base === 'valor' ? a.valor : a.quantidade);
    const vb = base === 'lucro' ? b.lucro : (base === 'valor' ? b.valor : b.quantidade);
    return ordenarAsc ? (va - vb) : (vb - va);
  });

  let acumulado = 0;
  return ordenados.map((item, idx) => {
    const baseValor = base === 'lucro' ? item.lucro : (base === 'valor' ? item.valor : item.quantidade);
    acumulado += baseValor;
    const percAcum = totalBase > 0 ? (acumulado / totalBase) * 100 : 0;
    const perc = totalBase > 0 ? (baseValor / totalBase) * 100 : 0;
    const classe = percAcum <= 80 ? 'A' : percAcum <= 95 ? 'B' : 'C';
    return {
      ...item,
      base: baseValor,
      perc,
      percAcum,
      classe,
      rank: idx + 1
    };
  });
};

const RelatorioProdutos: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const [dataInicio, setDataInicio] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [dataFim, setDataFim] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [formaPagamento, setFormaPagamento] = useState<'todos' | 'avista' | 'aprazo'>('todos');
  const [baseCalculo, setBaseCalculo] = useState<BaseCalculo>('valor');
  const [ordenarAsc, setOrdenarAsc] = useState<boolean>(false);
  const [todasFiliais, setTodasFiliais] = useState<boolean>(false);
  const [grupo, setGrupo] = useState<string>('todos');
  const [medida, setMedida] = useState<string>('todas');
  const [grupos, setGrupos] = useState<Array<{ Controle: number; Grupo: string }>>([]);

  const queryKey = useMemo(() => (
    ['relatorio-abc-produtos', todasFiliais ? 'todas' : (filialSelecionada?.id || '-'), dataInicio, dataFim, formaPagamento, grupo]
  ), [todasFiliais, filialSelecionada?.id, dataInicio, dataFim, formaPagamento, grupo]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    enabled: todasFiliais || !!filialSelecionada?.id,
    queryFn: async () => {
      const filtros = { dataInicio, dataFim, formaPagamento, grupo };
      if (todasFiliais) {
        const res = await apiService.obterRelatorioVendasTodas(filtros);
        return res?.data;
      }
      if (!filialSelecionada?.id) return null;
      const res = await apiService.obterRelatorioVendas(filialSelecionada.id, filtros);
      return res?.data;
    }
  });

  useEffect(() => {
    const carregar = async () => {
      if (!filialSelecionada?.id) return;
      try {
        const lista = await apiService.listarGrupos(filialSelecionada.id);
        setGrupos(lista);
      } catch {
        // silencioso
      }
    };
    carregar();
  }, [todasFiliais, filialSelecionada?.id]);

  const linhas: any[] = data?.dados || [];
  const isPneu = (linha: any) => {
    const g = String(linha?.Grupo ?? '').toLowerCase();
    return g.includes('pneu');
  };
  const linhasPneus = useMemo(() => linhas.filter(isPneu), [linhas]);
  const medidasDisponiveis = useMemo(() => {
    const uniq = new Map<string, { id: string; label: string }>();
    linhasPneus.forEach((l) => {
      const label = String(l?.Medida ?? l?.MedidaId ?? '').trim();
      const id = l?.MedidaId !== undefined && l?.MedidaId !== null ? String(l.MedidaId) : label;
      if (!label || !id) return;
      if (!uniq.has(id)) uniq.set(id, { id, label });
    });
    return Array.from(uniq.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [linhasPneus]);
  const linhasPneusFiltradas = useMemo(() => {
    if (!medida || medida === 'todas') return linhasPneus;
    return linhasPneus.filter((l) => String(l?.MedidaId ?? l?.Medida ?? '') === medida);
  }, [linhasPneus, medida]);
  const gruposDisponiveis = useMemo(() => grupos, [grupos]);

  const curvaProdutos = useMemo(() => montarCurva(linhas, 'produto', baseCalculo, ordenarAsc), [linhas, baseCalculo, ordenarAsc]);
  const curvaGrupos = useMemo(() => montarCurva(linhas, 'grupo', baseCalculo, ordenarAsc), [linhas, baseCalculo, ordenarAsc]);
  const curvaMedidas = useMemo(() => montarCurva(linhasPneusFiltradas, 'medida', baseCalculo, ordenarAsc), [linhasPneusFiltradas, baseCalculo, ordenarAsc]);

  const renderTabelaProdutos = (items: CurvaItem[]) => (
    <div style={{ overflowX: 'auto' }}>
      <Tabela>
        <thead>
          <tr>
            <Th>#</Th>
            <Th>Produto</Th>
            <Th>Grupo</Th>
            <Th>Quantidade</Th>
            <Th>Valor</Th>
            <Th style={{ minWidth: 150 }}>Lucro</Th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><Td colSpan={6}>Carregando...</Td></tr>
          ) : items.length === 0 ? (
            <tr><Td colSpan={6}>Nenhum registro encontrado</Td></tr>
          ) : (
            items.map((item) => (
              <tr key={item.id}>
                <Td>{item.rank}</Td>
                <Td>{item.nome}</Td>
                <Td>{item.grupo || 'Sem grupo'}</Td>
                <Td>{formatQuantidade(item.quantidade)}</Td>
                <Td>{formatMoeda(item.valor)}</Td>
                <Td>{formatMoeda(item.lucro)}</Td>
              </tr>
            ))
          )}
        </tbody>
      </Tabela>
    </div>
  );

  const renderTabelaGrupos = (items: CurvaItem[]) => (
    <div style={{ overflowX: 'auto' }}>
      <Tabela>
        <thead>
          <tr>
            <Th>#</Th>
            <Th>Grupo</Th>
            <Th>Quantidade</Th>
            <Th>Valor</Th>
            <Th style={{ minWidth: 150 }}>Lucro</Th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><Td colSpan={5}>Carregando...</Td></tr>
          ) : items.length === 0 ? (
            <tr><Td colSpan={5}>Nenhum registro encontrado</Td></tr>
          ) : (
            items.map((item) => (
              <tr key={item.id}>
                <Td>{item.rank}</Td>
                <Td>{item.nome}</Td>
                <Td>{formatQuantidade(item.quantidade)}</Td>
                <Td>{formatMoeda(item.valor)}</Td>
                <Td>{formatMoeda(item.lucro)}</Td>
              </tr>
            ))
          )}
        </tbody>
      </Tabela>
    </div>
  );

  const renderTabelaMedidas = (items: CurvaItem[]) => (
    <div style={{ overflowX: 'auto' }}>
      <Tabela>
        <thead>
          <tr>
            <Th>#</Th>
            <Th>Medida</Th>
            <Th>Quantidade</Th>
            <Th>Valor</Th>
            <Th style={{ minWidth: 150 }}>Lucro</Th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><Td colSpan={5}>Carregando...</Td></tr>
          ) : items.length === 0 ? (
            <tr><Td colSpan={5}>Nenhum registro encontrado</Td></tr>
          ) : (
            items.map((item) => (
              <tr key={item.id}>
                <Td>{item.rank}</Td>
                <Td>{item.nome}</Td>
                <Td>{formatQuantidade(item.quantidade)}</Td>
                <Td>{formatMoeda(item.valor)}</Td>
                <Td>{formatMoeda(item.lucro)}</Td>
              </tr>
            ))
          )}
        </tbody>
      </Tabela>
    </div>
  );

  return (
    <Container>
      <ReportHeader
        title="Grupo, Medidas e Produtos"
        subtitle={`${todasFiliais ? 'Todas as filiais' : (filialSelecionada?.nome || '')} • ${data?.totalRegistros ?? 0} registros`}
        rightActions={(
          <Acoes>
            <Botao onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw size={16} /> Atualizar
            </Botao>
          </Acoes>
        )}
      />

      <Filtros>
        <Campo>
          <Label>Data Início</Label>
          <Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
        </Campo>
        <Campo>
          <Label>Data Fim</Label>
          <Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
        </Campo>
        <Campo>
          <Label>Forma de Pagamento</Label>
          <Select value={formaPagamento} onChange={e => setFormaPagamento(e.target.value as 'todos' | 'avista' | 'aprazo')}>
            <option value="todos">Todos</option>
            <option value="avista">A vista</option>
            <option value="aprazo">A prazo</option>
          </Select>
        </Campo>
        <Campo>
          <Label>Base</Label>
          <Select value={baseCalculo} onChange={e => setBaseCalculo(e.target.value as BaseCalculo)}>
            <option value="valor">Faturamento</option>
            <option value="quantidade">Quantidade</option>
            <option value="lucro">Lucro</option>
          </Select>
        </Campo>
        <Campo>
          <Label>Ordenação</Label>
          <Select value={ordenarAsc ? 'asc' : 'desc'} onChange={e => setOrdenarAsc(e.target.value === 'asc')}>
            <option value="desc">Maior → menor</option>
            <option value="asc">Menor → maior</option>
          </Select>
        </Campo>
        <Campo>
          <Label>Grupo do Produto</Label>
          <Select value={grupo} onChange={e => setGrupo(e.target.value)} disabled={gruposDisponiveis.length === 0}>
            <option value="todos">Todos</option>
            {gruposDisponiveis.map(g => (
              <option key={g.Controle} value={String(g.Controle)}>{g.Grupo}</option>
            ))}
          </Select>
        </Campo>
        <Campo>
          <Label>Medida (Pneus)</Label>
          <Select value={medida} onChange={e => setMedida(e.target.value)} disabled={medidasDisponiveis.length === 0}>
            <option value="todas">Todas</option>
            {medidasDisponiveis.map(m => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </Select>
        </Campo>
        <Campo>
          <Label>&nbsp;</Label>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={todasFiliais}
              onChange={(e) => setTodasFiliais(e.target.checked)}
            />
            Todas as Filiais
          </CheckboxRow>
        </Campo>
        <Acoes>
          <Botao onClick={() => refetch()} disabled={isLoading || isFetching}>
            <Calendar size={16} /> Aplicar
          </Botao>
        </Acoes>
      </Filtros>

      <Section>
        <SectionTitle>Grupos</SectionTitle>
        {renderTabelaGrupos(curvaGrupos)}
      </Section>

      <Section>
        <SectionTitle>Medidas (Pneus)</SectionTitle>
        {renderTabelaMedidas(curvaMedidas)}
      </Section>

      <Section>
        <SectionTitle>Produtos</SectionTitle>
        {renderTabelaProdutos(curvaProdutos)}
      </Section>
    </Container>
  );
};

export default RelatorioProdutos;
